import { Link } from 'react-router-dom'
import { Flag } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { useSectionFlagsStore, type MergedSectionFlag } from '@/stores/section-flags-store'

interface EditorFlagBannerProps {
  bidId: string
  flag: MergedSectionFlag
  /** When read-only, the banner still shows but "Mark reviewed" is hidden. */
  readOnly: boolean
}

/**
 * Amber strip above the DraftEditor warning that an accepted addendum change
 * touched a requirement mapped to this section (mockup scene 7). "View changes"
 * deep-links to the addendum-review surface on the Analysis tab (DocumentList
 * pill pattern); "Mark reviewed" resolves the flag(s) with an optimistic remove.
 */
export function EditorFlagBanner({ bidId, flag, readOnly }: EditorFlagBannerProps) {
  const resolveFlag = useSectionFlagsStore((s) => s.resolveFlag)
  const isResolving = useSectionFlagsStore((s) => flag.flagIds.some((id) => s.resolving[id]))

  const label = flag.sourceLabel ?? 'an addendum change'
  const noun = flag.itemCount === 1 ? 'requirement' : 'requirements'

  const handleMarkReviewed = () => {
    // Merge-on-read can collapse several backend rows into one banner — resolve
    // every underlying flag so the marker fully clears. resolveFlag never throws;
    // it returns success per flag, so we aggregate here and toast at most once
    // per click regardless of how many underlying resolves failed.
    void Promise.all(flag.flagIds.map((id) => resolveFlag(id))).then((results) => {
      if (results.some((ok) => !ok)) {
        toast.error('Failed to mark section reviewed')
      }
    })
  }

  return (
    <div className="flex items-center gap-3 border-b border-[hsl(var(--status-warning)/0.3)] bg-[hsl(var(--status-warning)/0.1)] px-3 py-2 text-[13px]">
      <Flag className="h-4 w-4 shrink-0 text-[hsl(var(--status-warning))]" />
      <p className="flex-1 min-w-0 text-foreground">
        Affected by <span className="font-medium">{label}</span> — {flag.itemCount} mapped {noun} changed.
      </p>
      {flag.reconciliationRunId && (
        <Link
          to={`/app/bids/${bidId}/qualify?view=addendum-review&run=${flag.reconciliationRunId}`}
          className="shrink-0 text-[hsl(var(--link))] hover:underline"
        >
          View changes
        </Link>
      )}
      {!readOnly && (
        <Button
          variant="ghost"
          size="sm"
          onClick={handleMarkReviewed}
          disabled={isResolving}
          className="shrink-0"
        >
          Mark reviewed
        </Button>
      )}
    </div>
  )
}
